import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getStatistic } from "../api/dashboard.api";
import type {
  DashboardStatisticRequest,
  DashboardStatisticResponse,
} from "../types/dashboard-data.types";
import type { DashboardQueryState } from "./dashboard-query.types";

function createDashboardStatisticRequest(): DashboardStatisticRequest {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7;
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfNextWeek = new Date(startOfWeek);
  startOfNextWeek.setDate(startOfWeek.getDate() + 7);

  return { from: startOfWeek.toISOString(), to: startOfNextWeek.toISOString() };
}

export const useStatisticData = (): DashboardQueryState<DashboardStatisticResponse> => {
  const [data, setData] = useState<DashboardStatisticResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { run } = useLatestRequest();
  const dataRef = useRef<DashboardStatisticResponse | null>(null);
  const sessionRef = useRef(0);

  dataRef.current = data;

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      void forceRefresh;
      sessionRef.current += 1;
      const session = sessionRef.current;

      if (dataRef.current !== null) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await run((signal) =>
          getStatistic(createDashboardStatisticRequest(), { signal }),
        );
        if (session !== sessionRef.current) return;
        if (result === undefined) return;
        setData(result);
        setError(null);
      } catch (requestError) {
        if (session !== sessionRef.current) return;
        setError(getApiErrorMessage(requestError, "Failed to fetch statistics"));
      } finally {
        if (session === sessionRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [run],
  );

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
    return () => {
      sessionRef.current += 1;
    };
  }, [fetchData]);

  return { data, isLoading, isRefreshing, error, refetch };
};
